import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { PlayerEvaluations } from "../../components/testlib/PlayerEvaluations";
import { WellnessHistory } from "../../components/wellness/WellnessHistory";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Trash2, Save, X, Calendar, Edit2, Youtube, FileText, Video, Bookmark, Copy,
} from "lucide-react";
import AppShell from "../../components/AppShell";
import MonthCalendar from "../../components/MonthCalendar";
import SessionEditor from "../../components/SessionEditor";
import { api, formatErr } from "../../lib/api";
import { monthKey } from "../../lib/training";

const NUTRITION_DAY_TYPES = [
  { value: "partido", label: "Día de partido" },
  { value: "partido_suplente", label: "Partido suplente" },
  { value: "entreno_equipo", label: "Entreno con equipo" },
  { value: "fuerza", label: "Día de fuerza" },
  { value: "explosividad", label: "Día de explosividad" },
  { value: "recuperacion", label: "Día de recuperación" },
  { value: "movilidad", label: "Día de movilidad" },
];

const TEST_TYPES = [
  { value: "vertical_jump", label: "Salto vertical", unit: "cm" },
  { value: "sprint_10m", label: "Sprint 10m", unit: "s" },
  { value: "sprint_30m", label: "Sprint 30m", unit: "s" },
  { value: "squat_1rm", label: "Squat 1RM", unit: "kg" },
  { value: "bench_1rm", label: "Press banca 1RM", unit: "kg" },
  { value: "weight", label: "Peso corporal", unit: "kg" },
  { value: "body_fat", label: "% Grasa", unit: "%" },
  { value: "custom", label: "Personalizado", unit: "" },
];

const uid = () => Math.random().toString(36).slice(2, 11);

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "profile");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/clients/${id}`);
        setClient(data);
      } catch (err) {
        toast.error(formatErr(err));
        navigate("/app/admin/clients");
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  if (loading || !client) {
    return <AppShell><div style={{ color: "#737373" }}>Cargando...</div></AppShell>;
  }

  return (
    <AppShell>
      <Link to="/app/admin/clients" style={{ color: "#737373", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="back-to-clients">
        <ChevronLeft size={14} /> Volver a clientes
      </Link>
      <div className="qv-page-head" style={{ marginTop: 16 }}>
        <div className="label">Cliente</div>
        <h1 data-testid="client-name-header">{client.name}</h1>
        <p className="sub">{client.email} · {client.team || "—"} · {client.position || "—"}</p>
      </div>

      <div className="qv-tabs">
        {[
          ["profile", "Perfil"],
          ["training", "Entreno"],
          ["nutrition", "Nutrición"],
          ["evaluaciones", "Evaluaciones"],
          ["bienestar", "Bienestar"],
          ["tests", "Tests"],
          ["feedback", "Feedback"],
        ].map(([k, l]) => (
          <button key={k} className={`qv-tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab client={client} setClient={setClient} />}
      {tab === "training" && <TrainingTab clientId={client.id} />}
      {tab === "nutrition" && <NutritionTab clientId={client.id} />}
      {tab === "evaluaciones" && <PlayerEvaluations clientId={client.id} />}
      {tab === "bienestar" && <WellnessHistory clientId={client.id} />}
      {tab === "tests" && <TestsTab clientId={client.id} />}
      {tab === "feedback" && <FeedbackTab clientId={client.id} />}
    </AppShell>
  );
}

/* -------- PROFILE -------- */
function ProfileTab({ client, setClient }) {
  const [f, setF] = useState({ ...client });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/clients/${client.id}`, {
        name: f.name, phone: f.phone, age: f.age ? parseInt(f.age) : null,
        position: f.position, team: f.team, notes: f.notes,
        monthly_price_eur: f.monthly_price_eur ? parseFloat(f.monthly_price_eur) : null,
        subscription_status: f.subscription_status,
        subscription_renews_at: f.subscription_renews_at || null,
      });
      setClient(data);
      toast.success("Perfil actualizado");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm(`¿Eliminar definitivamente a ${client.name}? Esta acción borra entrenos, nutrición, tests y feedback.`)) return;
    try {
      await api.delete(`/admin/clients/${client.id}`);
      toast.success("Cliente eliminado");
      navigate("/app/admin/clients");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="qv-card-app" data-testid="profile-tab">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="qv-field"><label>Nombre</label><input value={f.name||""} onChange={(e)=>setF({...f,name:e.target.value})} /></div>
        <div className="qv-field"><label>Email</label><input value={f.email||""} disabled style={{ opacity: 0.6 }} /></div>
        <div className="qv-field"><label>Teléfono</label><input value={f.phone||""} onChange={(e)=>setF({...f,phone:e.target.value})} /></div>
        <div className="qv-field"><label>Edad</label><input type="number" value={f.age||""} onChange={(e)=>setF({...f,age:e.target.value})} /></div>
        <div className="qv-field"><label>Posición</label><input value={f.position||""} onChange={(e)=>setF({...f,position:e.target.value})} /></div>
        <div className="qv-field"><label>Equipo</label><input value={f.team||""} onChange={(e)=>setF({...f,team:e.target.value})} /></div>
        <div className="qv-field"><label>Precio mensual (€)</label><input type="number" step="0.01" value={f.monthly_price_eur||""} onChange={(e)=>setF({...f,monthly_price_eur:e.target.value})} /></div>
        <div className="qv-field">
          <label>Estado suscripción</label>
          <select value={f.subscription_status||"trial"} onChange={(e)=>setF({...f,subscription_status:e.target.value})}>
            <option value="trial">Prueba</option>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
          </select>
        </div>
        <div className="qv-field" style={{ gridColumn: "1/-1" }}>
          <label>Notas internas (solo tú las ves)</label>
          <textarea rows={3} value={f.notes||""} onChange={(e)=>setF({...f,notes:e.target.value})} />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <button onClick={remove} className="qv-btn-ghost" style={{ color: "var(--qv-red)", borderColor: "rgba(225,29,46,0.4)" }} data-testid="delete-client">
          <Trash2 size={14} /> ELIMINAR CLIENTE
        </button>
        <button onClick={save} disabled={saving} className="qv-btn-primary" data-testid="save-profile">
          <Save size={16} /> {saving ? "GUARDANDO..." : "GUARDAR"}
        </button>
      </div>
    </div>
  );
}

/* -------- TRAINING (monthly calendar) -------- */
function TrainingTab({ clientId }) {
  const [current, setCurrent] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [editing, setEditing] = useState(null); // { date, session }
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clients/${clientId}/sessions`, { params: { month: monthKey(current) } });
      setSessions(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId, current]);

  return (
    <div data-testid="training-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ color: "#a3a3a3", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          {loading ? "Cargando..." : `${sessions.length} sesiones este mes`}
        </div>
        <div style={{ fontSize: 11, color: "#737373" }}>
          Pulsa un día para crear o editar la sesión.
        </div>
      </div>

      <div className="qv-card-app">
        <MonthCalendar
          sessions={sessions}
          current={current}
          onChangeMonth={setCurrent}
          canAdd
          onDateClick={(date, s) => setEditing({ date, session: s })}
        />
      </div>

      {editing && (
        <SessionEditor
          clientId={clientId}
          date={editing.date}
          session={editing.session}
          onClose={() => setEditing(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

/* -------- NUTRITION -------- */
function FoodCol({ title, k, items, newFood, setNewFood, onAdd, onRemove }) {
  return (
    <div className="qv-card-app">
      <div className="qv-card-head"><h3>{title}</h3></div>
      <div className="qv-tag-list" style={{ marginBottom: 10 }}>
        {items.length === 0 && <div style={{ color: "#737373", fontSize: 12 }}>Sin alimentos aún</div>}
        {items.map((f, i) => (
          <span key={i} className="qv-tag">{f}<button onClick={() => onRemove(k, i)}><X size={12} /></button></span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          className="qv-input"
          style={{ height: 38, fontSize: 13 }}
          value={newFood[k]}
          onChange={(e) => setNewFood({ ...newFood, [k]: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd(k))}
          placeholder="Ej: Pollo"
          data-testid={`food-input-${k}`}
        />
        <button onClick={() => onAdd(k)} className="qv-btn-ghost" style={{ fontSize: 10 }}><Plus size={12} /></button>
      </div>
    </div>
  );
}

function NutritionTab({ clientId }) {
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newFood, setNewFood] = useState({
    proteins_foods: "", fats_foods: "", carbs_foods: "",
    legumes_foods: "", vegetables_foods: "", fruits_foods: "",
  });

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/admin/clients/${clientId}/nutrition`);
      setPlan(data);
    })();
  }, [clientId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/clients/${clientId}/nutrition`, {
        proteins_foods: plan.proteins_foods,
        fats_foods: plan.fats_foods,
        carbs_foods: plan.carbs_foods,
        legumes_foods: plan.legumes_foods,
        vegetables_foods: plan.vegetables_foods,
        fruits_foods: plan.fruits_foods,
        day_distributions: plan.day_distributions,
        notes: plan.notes,
      });
      toast.success("Nutrición guardada");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };
  const addFood = (key) => {
    const v = newFood[key].trim(); if (!v) return;
    setPlan((p) => ({ ...p, [key]: [...(p[key] || []), v] }));
    setNewFood({ ...newFood, [key]: "" });
  };
  const removeFood = (key, i) => setPlan((p) => ({ ...p, [key]: (p[key] || []).filter((_, idx) => idx !== i) }));
  const updateDist = (i, k, v) => setPlan((p) => { const d = [...p.day_distributions]; d[i] = { ...d[i], [k]: v }; return { ...p, day_distributions: d }; });

  if (!plan) return <div style={{ color: "#737373" }}>Cargando...</div>;

  return (
    <div data-testid="nutrition-tab">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
        <button onClick={save} disabled={saving} className="qv-btn-primary" data-testid="save-nutrition">
          <Save size={16} /> {saving ? "GUARDANDO..." : "GUARDAR"}
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 22 }}>
        <FoodCol title="PROTEÍNAS" k="proteins_foods" items={plan.proteins_foods || []} newFood={newFood} setNewFood={setNewFood} onAdd={addFood} onRemove={removeFood} />
        <FoodCol title="GRASAS" k="fats_foods" items={plan.fats_foods || []} newFood={newFood} setNewFood={setNewFood} onAdd={addFood} onRemove={removeFood} />
        <FoodCol title="CARBOHIDRATOS" k="carbs_foods" items={plan.carbs_foods || []} newFood={newFood} setNewFood={setNewFood} onAdd={addFood} onRemove={removeFood} />
        <FoodCol title="LEGUMBRES" k="legumes_foods" items={plan.legumes_foods || []} newFood={newFood} setNewFood={setNewFood} onAdd={addFood} onRemove={removeFood} />
        <FoodCol title="VERDURAS" k="vegetables_foods" items={plan.vegetables_foods || []} newFood={newFood} setNewFood={setNewFood} onAdd={addFood} onRemove={removeFood} />
        <FoodCol title="FRUTAS" k="fruits_foods" items={plan.fruits_foods || []} newFood={newFood} setNewFood={setNewFood} onAdd={addFood} onRemove={removeFood} />
      </div>

      <div className="qv-card-app">
        <div className="qv-card-head"><h3>Distribución por tipo de día</h3></div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
            <thead>
              <tr style={{ color: "#737373", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em" }}>
                <th style={{ textAlign: "left", padding: "10px 6px" }}>Tipo de día</th>
                <th style={{ padding: "10px 6px" }}>Kcal</th>
                <th style={{ padding: "10px 6px" }}>Proteína (g)</th>
                <th style={{ padding: "10px 6px" }}>Carbs (g)</th>
                <th style={{ padding: "10px 6px" }}>Grasas (g)</th>
                <th style={{ padding: "10px 6px" }}>Notas</th>
              </tr>
            </thead>
            <tbody>
              {plan.day_distributions.map((d, i) => {
                const label = NUTRITION_DAY_TYPES.find((dt) => dt.value === d.day_type)?.label || d.day_type;
                return (
                  <tr key={d.day_type} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <td style={{ padding: "8px 6px", fontWeight: 700 }}>{label}</td>
                    <td><input type="number" value={d.kcal} onChange={(e) => updateDist(i, "kcal", parseInt(e.target.value) || 0)} className="qv-input" style={{ height: 34, fontSize: 13, padding: "0 8px" }} /></td>
                    <td><input type="number" value={d.protein_g} onChange={(e) => updateDist(i, "protein_g", parseInt(e.target.value) || 0)} className="qv-input" style={{ height: 34, fontSize: 13, padding: "0 8px" }} /></td>
                    <td><input type="number" value={d.carbs_g} onChange={(e) => updateDist(i, "carbs_g", parseInt(e.target.value) || 0)} className="qv-input" style={{ height: 34, fontSize: 13, padding: "0 8px" }} /></td>
                    <td><input type="number" value={d.fat_g} onChange={(e) => updateDist(i, "fat_g", parseInt(e.target.value) || 0)} className="qv-input" style={{ height: 34, fontSize: 13, padding: "0 8px" }} /></td>
                    <td><input value={d.notes||""} onChange={(e) => updateDist(i, "notes", e.target.value)} className="qv-input" style={{ height: 34, fontSize: 13, padding: "0 8px" }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="qv-field" style={{ marginTop: 18 }}>
          <label>Notas generales</label>
          <textarea rows={3} value={plan.notes||""} onChange={(e) => setPlan({ ...plan, notes: e.target.value })} className="qv-input" style={{ height: "auto", padding: "10px 12px" }} />
        </div>
      </div>
    </div>
  );
}

/* -------- TESTS -------- */
const EMPTY_TEST = {
  test_type: "vertical_jump", label: "", value: "", unit: "cm", date: "",
  notes: "", description: "", tutorial_url: "", client_video_url: "",
};

function TestsTab({ clientId }) {
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // test id when editing
  const [form, setForm] = useState(EMPTY_TEST);
  const [templates, setTemplates] = useState([]);
  const [showTpls, setShowTpls] = useState(false);

  const load = async () => { const { data } = await api.get(`/admin/clients/${clientId}/tests`); setTests(data); };
  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/admin/test-templates");
      setTemplates(data);
    } catch { /* noop */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadTemplates(); }, [clientId]);

  const applyTemplate = (tpl) => {
    setForm((f) => ({
      ...f,
      test_type: tpl.test_type || f.test_type,
      label: tpl.label || "",
      unit: tpl.unit || f.unit,
      description: tpl.description || "",
      tutorial_url: tpl.tutorial_url || "",
    }));
    setShowTpls(false);
    toast.success(`Plantilla "${tpl.name}" cargada`);
  };

  const removeTemplate = async (tid, e) => {
    e.stopPropagation();
    if (!confirm("¿Eliminar esta plantilla?")) return;
    try {
      await api.delete(`/admin/test-templates/${tid}`);
      setTemplates((arr) => arr.filter((t) => t.id !== tid));
      toast.success("Plantilla eliminada");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const saveAsTemplate = async () => {
    const defaultName = TEST_TYPES.find((tt) => tt.value === form.test_type)?.label || form.label || "Mi test";
    const name = window.prompt("Nombre de la plantilla:", defaultName);
    if (!name || !name.trim()) return;
    try {
      const { data: created } = await api.post("/admin/test-templates", {
        name: name.trim(),
        test_type: form.test_type,
        label: form.label || null,
        unit: form.unit || "",
        description: form.description || null,
        tutorial_url: form.tutorial_url || null,
      });
      setTemplates((arr) => [created, ...arr]);
      toast.success("Plantilla guardada");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const onTypeChange = (v) => {
    const t = TEST_TYPES.find((tt) => tt.value === v);
    setForm((f) => ({ ...f, test_type: v, unit: t?.unit ?? f.unit }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_TEST });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      test_type: t.test_type,
      label: t.label || "",
      value: t.value != null ? String(t.value) : "",
      unit: t.unit || "",
      date: t.date ? new Date(t.date).toISOString().slice(0, 10) : "",
      notes: t.notes || "",
      description: t.description || "",
      tutorial_url: t.tutorial_url || "",
      client_video_url: t.client_video_url || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        test_type: form.test_type,
        label: form.label || null,
        value: form.value === "" ? null : parseFloat(form.value),
        unit: form.unit || "",
        date: form.date ? new Date(form.date).toISOString() : null,
        notes: form.notes || null,
        description: form.description || null,
        tutorial_url: form.tutorial_url || null,
        client_video_url: form.client_video_url || null,
      };
      if (editing) {
        await api.put(`/admin/tests/${editing}`, payload);
        toast.success("Test actualizado");
      } else {
        await api.post(`/admin/clients/${clientId}/tests`, payload);
        toast.success("Test guardado");
      }
      setOpen(false); setEditing(null);
      setForm({ ...EMPTY_TEST });
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este test?")) return;
    await api.delete(`/admin/tests/${id}`);
    toast.success("Eliminado"); load();
  };

  return (
    <div data-testid="tests-tab">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
        <button onClick={openCreate} className="qv-btn-primary" data-testid="add-test">
          <Plus size={16} /> AÑADIR TEST
        </button>
      </div>

      {tests.length === 0 ? (
        <div className="qv-empty">Aún no hay tests registrados</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tests.map((t) => {
            const label = TEST_TYPES.find((tt) => tt.value === t.test_type)?.label || t.label || t.test_type;
            const fromClient = t.created_by === "client";
            return (
              <div key={t.id} className="qv-card-app" style={{ padding: 14 }} data-testid={`test-${t.id}`}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{label}{t.test_type === "custom" && t.label ? ` · ${t.label}` : ""}</div>
                      {fromClient && <span style={{ fontSize: 9, color: "#22C55E", letterSpacing: "0.22em", fontWeight: 700, padding: "2px 6px", border: "1px solid rgba(34,197,94,0.4)" }}>ATLETA</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#737373", marginTop: 2, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      <Calendar size={10} style={{ display: "inline", marginRight: 4 }} />{new Date(t.date).toLocaleDateString("es-ES")}
                    </div>
                  </div>
                  <div style={{ fontFamily: "Bebas Neue", fontSize: "1.6rem", color: "var(--qv-red)", lineHeight: 1 }}>
                    {t.value != null ? t.value : <span style={{ color: "#737373", fontSize: "0.9rem" }}>—</span>}
                    {t.value != null && <span style={{ fontFamily: "Inter", fontSize: 12, color: "#a3a3a3", marginLeft: 4 }}>{t.unit}</span>}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {t.tutorial_url && <a href={t.tutorial_url} target="_blank" rel="noreferrer" className="qv-btn-ghost" style={{ fontSize: 10 }}><Youtube size={11} /> TUTOR.</a>}
                    {t.client_video_url && <a href={t.client_video_url} target="_blank" rel="noreferrer" className="qv-btn-ghost" style={{ fontSize: 10, borderColor: "rgba(34,197,94,0.4)", color: "#22C55E" }}><Video size={11} /> ATLETA</a>}
                    <button onClick={() => openEdit(t)} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "#cfcfcf", padding: "6px 8px", cursor: "pointer" }} data-testid={`edit-test-${t.id}`}><Edit2 size={12} /></button>
                    <button onClick={() => remove(t.id)} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "#737373", padding: "6px 8px", cursor: "pointer" }}><Trash2 size={12} /></button>
                  </div>
                </div>
                {t.description && (
                  <div style={{ marginTop: 10, padding: 10, background: "#060606", borderLeft: "2px solid rgba(225,29,46,0.4)", fontSize: 12, color: "#cfcfcf", whiteSpace: "pre-wrap" }}>
                    <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Instrucciones</div>
                    {t.description}
                  </div>
                )}
                {t.notes && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#cfcfcf", fontStyle: "italic" }}>"{t.notes}"</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="qv-modal-bg" onClick={() => setOpen(false)}>
          <form className="qv-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ maxWidth: 640 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ fontFamily: "Bebas Neue", fontSize: "1.8rem" }}>{editing ? "EDITAR TEST" : "NUEVO TEST"}</h3>
              <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer" }}><X size={20} /></button>
            </div>

            {/* Templates toolbar */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", position: "relative" }}>
              <button
                type="button"
                onClick={() => { loadTemplates(); setShowTpls((s) => !s); }}
                className="qv-btn-ghost"
                style={{ fontSize: 10 }}
                data-testid="test-load-template-btn"
              >
                <Copy size={12} /> CARGAR PLANTILLA {templates.length > 0 && <span style={{ color: "#EAB308", marginLeft: 4 }}>({templates.length})</span>}
              </button>
              <button
                type="button"
                onClick={saveAsTemplate}
                disabled={!form.description && !form.tutorial_url && !form.label}
                className="qv-btn-ghost"
                style={{ fontSize: 10, opacity: (!form.description && !form.tutorial_url && !form.label) ? 0.5 : 1 }}
                data-testid="test-save-template-btn"
                title="Rellena nombre, instrucciones o tutorial antes de guardar"
              >
                <Bookmark size={12} /> GUARDAR COMO PLANTILLA
              </button>

              {showTpls && (
                <div data-testid="test-templates-list" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 10, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)", maxHeight: 280, overflowY: "auto" }}>
                  {templates.length === 0 ? (
                    <div style={{ padding: 16, fontSize: 12, color: "#737373", textAlign: "center" }}>
                      Aún no tienes plantillas. Crea un test y pulsa "GUARDAR COMO PLANTILLA".
                    </div>
                  ) : (
                    templates.map((tpl) => (
                      <div
                        key={tpl.id}
                        onClick={() => applyTemplate(tpl)}
                        data-testid={`test-template-${tpl.id}`}
                        style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#101010")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{tpl.name}</div>
                          <div style={{ color: "#737373", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                            {tpl.test_type}{tpl.unit ? ` · ${tpl.unit}` : ""}{tpl.description ? " · con instrucciones" : ""}{tpl.tutorial_url ? " · con vídeo" : ""}
                          </div>
                        </div>
                        <button onClick={(e) => removeTemplate(tpl.id, e)} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer", padding: 4 }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label>Tipo</label>
                <select value={form.test_type} onChange={(e) => onTypeChange(e.target.value)}>
                  {TEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {form.test_type === "custom" && (
                <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Nombre del test</label><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ej: Test de Cooper, T-Test..." /></div>
              )}

              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}><FileText size={11} /> Instrucciones (paso a paso)</label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="1. Calienta 5 min...&#10;2. Colócate de pie...&#10;3. Salta lo más alto posible..."
                  data-testid="test-description"
                  style={{ height: "auto", padding: "10px 12px" }}
                />
              </div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}><Youtube size={11} color="#E11D2E" /> URL vídeo demostración (coach)</label>
                <input
                  value={form.tutorial_url}
                  onChange={(e) => setForm({ ...form, tutorial_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="test-tutorial-url"
                />
              </div>

              <div className="qv-field"><label>Valor (vacío = no medible)</label><input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Ej: 45" data-testid="test-value" /></div>
              <div className="qv-field"><label>Unidad</label><input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="cm, kg, s, %" /></div>
              <div className="qv-field"><label>Fecha (vacío=hoy)</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>

              <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Notas</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

              {editing && (
                <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#22C55E" }}><Video size={11} /> Vídeo subido por el atleta</label>
                  <input value={form.client_video_url} onChange={(e) => setForm({ ...form, client_video_url: e.target.value })} placeholder="URL del vídeo del atleta" />
                </div>
              )}
            </div>
            <button type="submit" className="qv-btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 20 }} data-testid="test-submit">
              <Save size={14} /> {editing ? "ACTUALIZAR" : "GUARDAR TEST"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/* -------- FEEDBACK -------- */
function FeedbackTab({ clientId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => { const { data } = await api.get(`/admin/clients/${clientId}/feedback`); setItems(data); })();
  }, [clientId]);

  if (items.length === 0) return <div className="qv-empty">El cliente aún no ha enviado feedback</div>;

  return (
    <div data-testid="feedback-tab" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((f) => (
        <div key={f.id} className="qv-card-app">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 12, color: "#737373", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.2em" }}>
              <Calendar size={12} /> {new Date(f.date).toLocaleString("es-ES")}
              {f.workout_day && <span>· {f.workout_day}</span>}
            </div>
            {f.pain && <span className="qv-pill" style={{ color: "var(--qv-red)", borderColor: "rgba(225,29,46,0.5)" }}>Dolor: {f.pain_zone || "sí"}</span>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 14, marginTop: 14 }}>
            <div className="qv-stat"><div className="lbl">RPE</div><div className="val">{f.rpe}<span className="unit">/10</span></div></div>
            <div className="qv-stat"><div className="lbl">Energía</div><div className="val">{f.energy}<span className="unit">/5</span></div></div>
            <div className="qv-stat"><div className="lbl">Sueño</div><div className="val">{f.sleep_hours}<span className="unit">h</span></div></div>
            <div className="qv-stat"><div className="lbl">Estrés</div><div className="val">{f.stress}<span className="unit">/5</span></div></div>
          </div>
          {f.notes && <div style={{ marginTop: 14, padding: 12, background: "#060606", border: "1px solid rgba(255,255,255,0.05)", fontSize: 13, color: "#cfcfcf" }}>"{f.notes}"</div>}
        </div>
      ))}
    </div>
  );
}
